import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CoursesPage.css';

// Sample course data (would typically come from an API)
const sampleCourses = [
  {
    id: 1,
    title: 'Introduction to Agentic AI',
    description: 'Discover the core principles of Agentic AI, understand how agent-based systems work, and learn the key differences between Agentic and traditional AI approaches. This course lays the foundation for building intelligent, autonomous agents.',
    image: 'https://images.unsplash.com/photo-1580894732444-8ecded7900cd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80',
    duration: '4 weeks',
    level: 'Beginner'
  },
  {
    id: 2,
    title: 'Building AI Agents with LangGraph',
    description: 'Learn to design, develop, and deploy AI agents using the LangGraph framework. This course covers agent architectures, multi-agent collaboration, and practical implementation strategies for real-world applications.',
    image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80',
    duration: '6 weeks',
    level: 'Intermediate'
  },
  {
    id: 3,
    title: 'Build AI Agents Using CrewAI',
    description: 'Master advanced agent-building techniques with CrewAI. Explore complex agent systems, multi-agent coordination, and optimization strategies to create robust, scalable AI solutions.',
    image: 'https://images.unsplash.com/photo-1580894732444-8ecded7900cd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80',
    duration: '8 weeks',
    level: 'Advanced'
  }
];
const CoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [levelFilter, setLevelFilter] = useState('all');
  const [durationFilter, setDurationFilter] = useState('all');

  useEffect(() => {
    // In a real app, this would be an API call
    setCourses(sampleCourses);
  }, []);

  const handleFilterChange = () => {
    let filteredCourses = [...sampleCourses];

    // Apply level filter
    if (levelFilter !== 'all') {
      filteredCourses = filteredCourses.filter(course => 
        course.level.toLowerCase() === levelFilter.toLowerCase()
      );
    }

    // Apply duration filter
    if (durationFilter !== 'all') {
      const weekRanges = {
        'short': [1, 4],
        'medium': [5, 8],
        'long': [9, 100]
      };

      filteredCourses = filteredCourses.filter(course => {
        const weeks = parseInt(course.duration.split(' ')[0]);
        const range = weekRanges[durationFilter];
        return weeks >= range[0] && weeks <= range[1];
      });
    }

    setCourses(filteredCourses);
  };

  return (
    <main>
      <section className="courses-hero">
        <div className="container">
          <h2>Agentic AI Curriculum</h2>
          <p>Comprehensive courses to master the art and science of Agentic AI</p>
        </div>
      </section>

      <section className="courses-list">
        <div className="container">
          <div className="courses-filter">
            <h3>Filter Courses</h3>
            <div className="filter-options">
              <div className="filter-group">
                <label htmlFor="level-filter">Level:</label>
                <select 
                  id="level-filter" 
                  value={levelFilter}
                  onChange={(e) => setLevelFilter(e.target.value)}
                >
                  <option value="all">All Levels</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor="duration-filter">Duration:</label>
                <select 
                  id="duration-filter"
                  value={durationFilter}
                  onChange={(e) => setDurationFilter(e.target.value)}
                >
                  <option value="all">Any Duration</option>
                  <option value="short">1-4 weeks</option>
                  <option value="medium">5-8 weeks</option>
                  <option value="long">9+ weeks</option>
                </select>
              </div>
              <button 
                className="btn-filter"
                onClick={handleFilterChange}
              >
                Apply Filters
              </button>
            </div>
          </div>

          <div className="course-grid">
            {courses.length > 0 ? (
              courses.map(course => (
                <div className="course-card" key={course.id}>
                  <div className="course-image">
                    <img src={course.image} alt={course.title} />
                  </div>
                  <div className="course-content">
                    <h3>{course.title}</h3>
                    <p>{course.description}</p>
                    <div className="course-preview">
                      <h4>What you'll learn:</h4>
                      <ul>
                        {course.id === 1 && (
                          <>
                            <li>Fundamentals of AI agents</li>
                            <li>Agent architectures</li>
                            <li>Decision-making algorithms</li>
                          </>
                        )}
                        {course.id === 2 && (
                          <>
                            <li>Agent development environments</li>
                            <li>Building your first AI agent</li>
                            <li>Testing and debugging techniques</li>
                          </>
                        )}
                        {course.id === 3 && (
                          <>
                            <li>Complex agent systems</li>
                            <li>Multi-agent coordination</li>
                            <li>Advanced optimization strategies</li>
                          </>
                        )}
                      </ul>
                    </div>
                    <div className="course-meta">
                      <span>{course.duration}</span>
                      <span>{course.level}</span>
                    </div>
                    <Link to={`/course/${course.id}`} className="btn-course">View Course</Link>
                  </div>
                </div>
              ))
            ) : (
              <p className="no-courses">No courses match your filter criteria. Please try different filters.</p>
            )}
          </div>
        </div>
      </section>

      <section className="course-path">
        <div className="container">
          <h2>Recommended Learning Path</h2>
          <div className="path-steps">
            <div className="path-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Introduction to Agentic AI</h3>
                <p>Start with the fundamentals and core concepts</p>
              </div>
            </div>
            <div className="path-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Building AI Agents</h3>
                <p>Learn practical implementation techniques</p>
              </div>
            </div>
            <div className="path-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Advanced Agent Architectures</h3>
                <p>Master complex systems and architectures</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CoursesPage;
